<script setup lang="ts">
import { onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useUserStore } from '@/stores/user';
import { consumeReturnTo } from '@/services/return-to';
import { getUnavailableAccess } from '@/services/access-unavailable';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const userStore = useUserStore();

onMounted(async () => {
  const token = route.params.token as string;

  if (token) {
    try {
      // Determine if this is a prospect based on the URL or other criteria
      const isProspect =
        route.query.prospect === 'true' ||
        route.path.includes('helpee') ||
        route.path.includes('prospect');

      userStore.isProspect = isProspect;

      // Get JWT token using the provided token
      await authStore.getJwtToken(token, isProspect);

      // If successful, redirect to the appropriate dashboard
      if (authStore.jwtToken) {
        const returnTo = consumeReturnTo();
        router.push(returnTo ?? { name: 'relations-empty-state' });
      } else {
        // If token is invalid, redirect to login
        router.push({ name: 'login' });
      }
    } catch (error) {
      if (getUnavailableAccess(error)) return; // The HTTP layer is opening the explanation screen.
      console.error('Token authentication failed:', error);
      // Redirect to login on error
      router.push({ name: 'login' });
    }
  } else {
    // No token provided, redirect to login
    router.push({ name: 'login' });
  }
});
</script>

<template>
  <div class="flex items-center justify-center min-h-screen">
    <div class="text-center">
      <div
        class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"
      ></div>
      <p class="mt-4 text-gray-600">Authenticating...</p>
    </div>
  </div>
</template>
